import { AiOutlineLoading } from '@react-icons/all-files/ai/AiOutlineLoading'
import { FaFolder } from '@react-icons/all-files/fa/FaFolder'
import { GoTrash } from '@react-icons/all-files/go/GoTrash'
import { MdError } from '@react-icons/all-files/md/MdError'
import dayjs from 'dayjs'
import pako from 'pako'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FileUploadIcon } from '~/constants/icon'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { uploadFile } from '~/services/organizationService'
import { isTar } from '~/utils/blob-types'
import { getDeviceId } from '~/utils/storage'
import {
  clearStoredTarBlob,
  createCompressedTarArchive as createTarArchive
} from '~/utils/tarUtils'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { useFolderPicker } from '~/hooks/useFolderPicker'
import { bridge } from '~/services/bridge'
import GitIgnore from './.gitignore'

interface MuleProjectUploadProps {
  onUploadComplete?: (uploadedPaths: string[]) => void
  className?: string
  withExamplePrefilled?: boolean
  initialUploads?: { name: string; s3Path: string }[]
  multiRepo?: boolean
  disabled?: boolean
}

// Virus scan status values from the backend
enum VirusScanStatus {
  NO_THREATS_FOUND = 'NO_THREATS_FOUND',
  THREATS_FOUND = 'THREATS_FOUND',
  SCAN_SKIPPED = 'SCAN_SKIPPED',
  PENDING = 'PENDING'
}

interface RepositoryUpload {
  id: string
  name: string
  file: File
  s3Path?: string
  progress: number
  status: 'pending' | 'uploading' | 'scanning' | 'completed' | 'error'
  errorMessage?: string
  virusScanStatus?: string
  uploadStatus?: string
}

function resolvePluginAssetUrl(assetPath: string): string {
  const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`
  const runtimeWindow = window as Window & {
    CURIETECH_WEBVIEW_DEV_SERVER?: string
    CURIETECH_BASE_URL?: string
  }

  // In plugin dev mode, assets are served from the local dev server.
  if (runtimeWindow.CURIETECH_WEBVIEW_DEV_SERVER) {
    return `${runtimeWindow.CURIETECH_WEBVIEW_DEV_SERVER.replace(/\/$/, '')}${normalizedPath}`
  }

  // In plugin server mode, load static assets from platform base URL.
  if (bridge.isInPlugin() && runtimeWindow.CURIETECH_BASE_URL) {
    return `${runtimeWindow.CURIETECH_BASE_URL.replace(/\/$/, '')}${normalizedPath}`
  }

  // Browser mode uses same-origin assets.
  return normalizedPath
}

export async function loadExampleProjectAsFile(): Promise<{
  fileName: string
  file: File
}> {
  const candidateUrls = [
    resolvePluginAssetUrl('/assets/example-repository.tar.gz'),
    '/assets/example-repository.tar.gz'
  ]

  let response: Response | null = null
  for (const url of candidateUrls) {
    try {
      const res = await fetch(url)
      if (res.ok) {
        response = res
        break
      }
    } catch {
      // Try next candidate URL
    }
  }

  if (!response) {
    throw new Error('Failed to fetch example project from configured asset URLs')
  }

  const blob = await response.blob()
  const blobArray = new Uint8Array(await blob.arrayBuffer())

  let gzippedTar: Uint8Array = blobArray
  if (isTar(blobArray)) {
    gzippedTar = pako.gzip(blobArray)
  }

  const clientId = getDeviceId()
  const date = dayjs().format('YYYYMMDDHHmmss')
  const fileName = 'example-mule-project.tar.gz'
  const newFileName = `${clientId}-${date}___${fileName}`

  return {
    fileName,
    file: new File([gzippedTar as unknown as ArrayBuffer], newFileName, {
      type: 'application/gzip'
    })
  }
}

export async function loadExampleProjectAsS3Path(
  file: File,
  workspaceUuid: string,
  onVirusScanStatus?: (status: string, message?: string) => void
): Promise<string> {
  return new Promise((resolve, reject) => {
    uploadFile(workspaceUuid, file, {
      onProgress: () => {},
      onComplete: uploadedPath => {
        resolve(uploadedPath)
      },
      onError: error => {
        BetterToast.error('Failed to load example project')
        reject(error)
      },
      onVirusScanStatus: (status, message) => {
        if (onVirusScanStatus) onVirusScanStatus(status, message)
      }
    })
  })
}

export default function MuleProjectUpload({
  onUploadComplete = () => {},
  className,
  withExamplePrefilled = false,
  initialUploads = [],
  multiRepo = false,
  disabled = false
}: MuleProjectUploadProps) {
  const isPlugin = useCompactViewport()
  const { pickFolder } = useFolderPicker()
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid
  const [searchParams] = useSearchParams()
  const ideS3Path = searchParams.get('s3_tar_file_path')
  const [dragOver, setDragOver] = useState(false)
  const [projectName, setProjectName] = useState<string | null>(null)
  const [uploadStatus, setUploadStatus] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [internalVirusScanStatus, setInternalVirusScanStatus] = useState<string | null>(null)
  const isThreatsFound = internalVirusScanStatus === VirusScanStatus.THREATS_FOUND
  const [singleRepoS3Path, setSingleRepoS3Path] = useState<string>('')
  const [hasInitializedRepositories, setHasInitializedRepositories] = useState(false)

  // Detailed progress tracking (used for future enhancements)
  const [, setDetailedProgress] = useState<{
    stage: string
    current: number
    total: number
    fileSize?: number
    startTime?: number
  } | null>(null)

  // Virus scan progress tracking
  const [virusScanProgress, setVirusScanProgress] = useState<number | null>(null)
  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  // Multi-repository state
  const [repositories, setRepositories] = useState<RepositoryUpload[]>([])

  // Track if example has been loaded to prevent infinite loops
  const exampleLoadedRef = React.useRef(false)

  const generateId = (): string => Math.random().toString(36).substr(2, 9)

  // Format detailed progress message
  const formatProgressMessage = useCallback(
    (stage: string, current: number, total: number, fileSize?: number): string => {
      const percentage = total > 0 ? Math.round((current / total) * 100) : 0

      switch (stage) {
        case 'Reading files':
          return `Reading files: ${current}/${total} (${percentage}%)`
        case 'Creating archive':
          return 'Creating archive...'
        case 'Compressing':
          if (fileSize) {
            const sizeMB = (fileSize / (1024 * 1024)).toFixed(1)
            return `Compressing archive (${sizeMB} MB)...`
          }
          return 'Compressing archive...'
        default:
          return stage
      }
    },
    []
  )

  // Calculate ETA
  const calculateETA = useCallback(
    (startTime: number, current: number, total: number): string | null => {
      if (current === 0 || total === 0) return null
      const elapsed = Date.now() - startTime
      const rate = current / elapsed
      const remaining = (total - current) / rate
      const seconds = Math.ceil(remaining / 1000)
      if (seconds < 5) return null
      if (seconds < 60) return `${seconds}s remaining`
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${minutes}m ${secs}s remaining`
    },
    []
  )

  // Initialize from initialUploads prop (for edit mode or pre-populated state)
  useEffect(() => {
    if (!hasInitializedRepositories && initialUploads && initialUploads.length > 0) {
      if (multiRepo) {
        // Multi-repo mode: set all uploads as repositories
        const initialRepos: RepositoryUpload[] = initialUploads.map(repo => ({
          id: generateId(),
          name: repo.name,
          file: new File([], repo.name),
          s3Path: repo.s3Path,
          progress: 100,
          status: 'completed' as const,
          uploadStatus: 'Previously uploaded'
        }))
        setRepositories(initialRepos)
      } else {
        // Single repo mode: use the first upload
        const firstUpload = initialUploads[0]
        const fullFileName = firstUpload.s3Path.split('/').pop() || 'Uploaded Project'
        let displayName = fullFileName

        if (fullFileName.includes('___')) {
          const parts = fullFileName.split('___')
          if (parts.length > 1) {
            displayName = parts[1].replace('.tar.gz', '')
            const firstPart = parts[0]
            const timestampMatch = firstPart.match(/-(\d{14})$/)
            if (timestampMatch) {
              const timestamp = timestampMatch[1]
              displayName = `${displayName}_${timestamp}.tar.gz`
            }
          }
        }

        setProjectName(displayName)
        setSingleRepoS3Path(firstUpload.s3Path)
      }
      setHasInitializedRepositories(true)
    }
  }, [initialUploads, hasInitializedRepositories, multiRepo])

  // Initialize from URL query param (s3_tar_file_path)
  useEffect(() => {
    if (!ideS3Path || hasInitializedRepositories || projectName || initialUploads.length > 0) {
      return
    }

    // Extract filename from s3 path for display
    const fullFileName = ideS3Path.split('/').pop() || 'IDE Upload'
    let displayName = fullFileName

    // Parse the filename format: {uuid}-{timestamp}___{originalName}.tar.gz
    if (fullFileName.includes('___')) {
      const parts = fullFileName.split('___')
      if (parts.length > 1) {
        displayName = parts[1].replace('.tar.gz', '')
        const firstPart = parts[0]
        const timestampMatch = firstPart.match(/-(\d{14})$/)
        if (timestampMatch) {
          const timestamp = timestampMatch[1]
          displayName = `${displayName}_${timestamp}.tar.gz`
        }
      }
    }

    if (multiRepo) {
      // Multi-repo mode: add as a completed repository
      const initialRepo: RepositoryUpload = {
        id: generateId(),
        name: displayName,
        file: new File([], displayName),
        s3Path: ideS3Path,
        progress: 100,
        status: 'completed' as const,
        uploadStatus: 'Previously uploaded from IDE'
      }
      setRepositories([initialRepo])
      onUploadComplete([ideS3Path])
    } else {
      // Single repo mode: set project name and s3 path
      setProjectName(displayName)
      setSingleRepoS3Path(ideS3Path)
      onUploadComplete([ideS3Path])
    }

    setHasInitializedRepositories(true)
  }, [
    ideS3Path,
    hasInitializedRepositories,
    projectName,
    initialUploads,
    multiRepo,
    onUploadComplete
  ])

  // Shared processing function for both single and multi-repo (matches original validation exactly)
  const processRepository = useCallback(
    async (
      files: FileList,
      isDropped: boolean = false
    ): Promise<{ tarFile: File; originalFilename: string } | null> => {
      try {
        const startTime = Date.now()
        const { tarFile, originalFilename } = await createTarArchive(
          files,
          GitIgnore,
          isDropped,
          (stage, current, total) => {
            setDetailedProgress({
              stage,
              current,
              total,
              startTime
            })
            const message = formatProgressMessage(stage, current, total)
            const eta = calculateETA(startTime, current, total)
            setUploadStatus(eta ? `${message} • ${eta}` : message)
          }
        )
        return { tarFile, originalFilename }
      } catch (error) {
        if (error instanceof Error) {
          BetterToast.error(`Failed to create archive: ${error.message}`)
        } else {
          BetterToast.error('Failed to create compressed tar archive of the project')
        }
        return null
      }
    },
    [formatProgressMessage, calculateETA]
  )

  // Handle threats found
  useEffect(() => {
    if (isThreatsFound) {
      setErrorMessage(
        'Security threat detected. Files have been removed for security. Please try with a different project.'
      )
    }
  }, [isThreatsFound])

  const handleClear = useCallback(() => {
    if (withExamplePrefilled) return

    setProjectName(null)
    setUploadStatus(null)
    setErrorMessage(null)
    setInternalVirusScanStatus(null)
    setSingleRepoS3Path('')
    setVirusScanProgress(null)
    setUploadProgress(null)
    clearStoredTarBlob()

    onUploadComplete([])
  }, [withExamplePrefilled, onUploadComplete])

  const uploadSingleRepository = useCallback(
    async (repo: RepositoryUpload) => {
      return new Promise<string>((resolve, reject) => {
        setRepositories(prev =>
          prev.map(r =>
            r.id === repo.id ? { ...r, status: 'uploading', uploadStatus: 'Uploading...' } : r
          )
        )

        const callbacks = {
          onProgress: (progress: number) => {
            setRepositories(prev =>
              prev.map(r =>
                r.id === repo.id
                  ? {
                      ...r,
                      progress,
                      uploadStatus:
                        progress < 100
                          ? `Uploading (${progress.toFixed(0)}%)`
                          : 'Scanning for security threats...'
                    }
                  : r
              )
            )
          },
          onComplete: (uploadedPath: string, virusScanStatus?: string) => {
            setRepositories(prev => {
              const next: RepositoryUpload[] = prev.map<RepositoryUpload>(r =>
                r.id === repo.id
                  ? {
                      ...r,
                      status: 'completed' as const,
                      s3Path: uploadedPath,
                      virusScanStatus,
                      uploadStatus: 'Upload complete',
                      progress: 100
                    }
                  : r
              )

              // Notify parent with completed paths
              if (multiRepo) {
                const completedPaths = next
                  .filter((r: RepositoryUpload) => r.status === 'completed' && r.s3Path)
                  .map((r: RepositoryUpload) => r.s3Path!)

                setTimeout(() => onUploadComplete(completedPaths), 0)
              }

              return next
            })
            resolve(uploadedPath)
          },
          onError: (error: string, virusScanStatus?: string) => {
            const errorMsg =
              virusScanStatus === VirusScanStatus.THREATS_FOUND
                ? 'Security threat detected. File has been removed for security.'
                : 'Upload failed. Please try again.'

            setRepositories(prev =>
              prev.map(r =>
                r.id === repo.id
                  ? {
                      ...r,
                      status: 'error',
                      errorMessage: errorMsg,
                      virusScanStatus,
                      uploadStatus: 'Upload failed'
                    }
                  : r
              )
            )

            if (virusScanStatus === VirusScanStatus.THREATS_FOUND) {
              BetterToast.error(`Security threat detected in ${repo.name}`)
            } else {
              BetterToast.error(`Failed to upload ${repo.name}: ${error}`)
            }

            reject(new Error(error))
          },
          onVirusScanStatus: (status: string) => {
            setRepositories(prev =>
              prev.map(r =>
                r.id === repo.id
                  ? {
                      ...r,
                      virusScanStatus: status,
                      uploadStatus:
                        status === 'PENDING' ? 'Scanning for security threats...' : r.uploadStatus
                    }
                  : r
              )
            )
          }
        }

        if (!workspaceUuid) {
          reject(new Error('No workspace selected'))
          return
        }
        uploadFile(workspaceUuid, repo.file, callbacks)
      })
    },
    [workspaceUuid, multiRepo, onUploadComplete]
  )

  // Multi-repository functions
  const handleMultipleFileSelection = useCallback(
    async (files: FileList) => {
      // Use shared processing function (matches original single repo validation exactly)
      const result = await processRepository(files, false)
      if (!result) return

      const { tarFile, originalFilename } = result

      const repo: RepositoryUpload = {
        id: generateId(),
        name: originalFilename.replace('.tar', '.tar.gz'),
        file: tarFile,
        progress: 0,
        status: 'pending',
        uploadStatus: 'Ready to upload'
      }

      // Add to repositories and immediately upload
      setRepositories(prev => [...prev, repo])

      // Short timeout to ensure UI updates before starting upload
      setTimeout(async () => {
        try {
          await uploadSingleRepository(repo)
          // No need to update repositories state here - uploadSingleRepository already does it
        } catch (error) {
          console.error('Failed to upload repository:', error)
        }
      }, 10)
    },
    [processRepository, uploadSingleRepository]
  )

  const removeRepository = useCallback(
    (id: string) => {
      setRepositories(prev => {
        const filteredRepos = prev.filter(r => r.id !== id)

        clearStoredTarBlob()

        if (multiRepo) {
          const completedPaths = filteredRepos
            .filter(r => r.status === 'completed' && r.s3Path)
            .map(r => r.s3Path!)

          setTimeout(() => onUploadComplete(completedPaths), 0)
        }

        return filteredRepos
      })
    },
    [multiRepo, onUploadComplete]
  )

  const onTarFileChange = useCallback(
    (tarFile: File | null) => {
      if (!tarFile) {
        return
      }

      setErrorMessage(null)
      const fileSizeMB = (tarFile.size / (1024 * 1024)).toFixed(1)
      const actionText = withExamplePrefilled ? 'Preparing to load' : 'Preparing to upload'
      setUploadStatus(`${actionText} (${fileSizeMB} MB)...`)
      setVirusScanProgress(null) // Reset scan progress at start
      setUploadProgress(0) // Initialize upload progress

      const uploadStartTime = Date.now()
      let lastProgressTime = uploadStartTime
      let lastProgress = 0

      // All uploads now use presigned URL approach (direct S3 upload with real progress)
      const callbacks = {
        onProgress: (progress: number) => {
          if (progress < 100) {
            const now = Date.now()
            const progressDiff = progress - lastProgress
            const timeDiff = (now - lastProgressTime) / 1000

            // Calculate upload speed
            let speedInfo = ''
            if (timeDiff > 0 && progressDiff > 0) {
              const bytesUploaded = (progressDiff / 100) * tarFile.size
              const speedMBps = bytesUploaded / timeDiff / (1024 * 1024)
              speedInfo = ` • ${speedMBps.toFixed(1)} MB/s`

              // Calculate ETA
              const remainingPercent = 100 - progress
              const remainingBytes = (remainingPercent / 100) * tarFile.size
              const etaSeconds = Math.ceil(remainingBytes / (speedMBps * 1024 * 1024))
              if (etaSeconds > 0 && etaSeconds < 300) {
                // Only show ETA if less than 5 minutes
                speedInfo += ` • ${etaSeconds}s remaining`
              }
            }

            const actionText = withExamplePrefilled ? 'Loading example' : 'Uploading'
            setUploadStatus(`${actionText} ${fileSizeMB} MB (${progress.toFixed(0)}%)${speedInfo}`)
            setUploadProgress(progress)

            lastProgressTime = now
            lastProgress = progress
          } else {
            setUploadStatus('Upload complete • Scanning for security threats...')
            setUploadProgress(100)
          }
        },
        onComplete: (uploadedPath: string, scanStatus?: string) => {
          setInternalVirusScanStatus(scanStatus || null)
          setUploadStatus(null)
          setUploadProgress(null) // Clear upload progress
          setProjectName(tarFile.name.replace('.tar.gz', '').split('___').pop()!)
          setSingleRepoS3Path(uploadedPath)
          onUploadComplete([uploadedPath])
        },
        onError: (error: string, scanStatus?: string) => {
          if (scanStatus === VirusScanStatus.THREATS_FOUND) {
            setErrorMessage('Security threat detected. File has been removed for security.')
            BetterToast.error('Security threat detected in uploaded file.')
          } else {
            setErrorMessage('Upload failed. Please try again.')
            BetterToast.error(`Failed to upload: ${error}`)
          }

          setUploadStatus(null)
          setUploadProgress(null) // Clear upload progress on error
          setProjectName(null)
          setInternalVirusScanStatus(scanStatus || null)
          setSingleRepoS3Path('')
          onUploadComplete([])
        },
        onVirusScanStatus: (status: string, message?: string) => {
          setInternalVirusScanStatus(status)

          if (status === VirusScanStatus.PENDING) {
            // Extract progress percentage from message if present
            const progressMatch = message?.match(/(\d+)% scanned/)
            if (progressMatch) {
              const progress = parseInt(progressMatch[1])
              setVirusScanProgress(progress)
              // Always show a clear scanning message
              setUploadStatus(message || `🔍 Security scan in progress (${progress}%)`)
            } else {
              // Show default scanning message if no progress info
              setUploadStatus(message || '🔍 Scanning for security threats...')
              // If we have a previous progress, keep showing it
              setVirusScanProgress(prev => (prev === null ? 0 : prev)) // Initialize progress bar at 0%
            }
          } else if (status === VirusScanStatus.NO_THREATS_FOUND) {
            setVirusScanProgress(100) // Show 100% before clearing
            setUploadStatus('✓ Security scan complete • No threats found')
            setTimeout(() => {
              setUploadStatus(null)
              setVirusScanProgress(null)
            }, 2000)
          } else if (status === VirusScanStatus.SCAN_SKIPPED) {
            setVirusScanProgress(null)
            setUploadStatus('✓ Upload complete • Security scan skipped')
            setTimeout(() => setUploadStatus(null), 2000)
          }
        }
      }

      if (!workspaceUuid) {
        setErrorMessage('No workspace selected')
        BetterToast.error('No workspace selected')
        setUploadStatus(null)
        setProjectName(null)
        return
      }
      uploadFile(workspaceUuid, tarFile, callbacks)
    },
    [withExamplePrefilled, workspaceUuid, onUploadComplete]
  )

  const createCompressedTarArchive = useCallback(
    async (files: FileList, isDropped: boolean = false) => {
      // Check if an upload is already in progress
      const isUploadActive = uploadStatus !== null || (projectName !== null && !singleRepoS3Path)

      if (isUploadActive) {
        return
      }

      // Use shared processing function (matches original validation exactly)
      const result = await processRepository(files, isDropped)
      if (!result) {
        // Failed to create tar - clear status
        setUploadStatus(null)
        return
      }

      const { tarFile, originalFilename } = result
      setProjectName(originalFilename.replace('.tar', '.tar.gz'))
      // Upload status will be updated in onTarFileChange callbacks
      onTarFileChange(tarFile)
    },
    [processRepository, onTarFileChange, uploadStatus, projectName, singleRepoS3Path]
  )

  // Reset example loaded flag when withExamplePrefilled changes
  useEffect(() => {
    if (!withExamplePrefilled) {
      exampleLoadedRef.current = false
    }
  }, [withExamplePrefilled])

  // Load example project on mount if withExamplePrefilled is true
  useEffect(() => {
    // Prevent infinite loops by checking if we've already started loading
    if (!withExamplePrefilled || !workspaceUuid || projectName || exampleLoadedRef.current) {
      return
    }

    // Mark as loaded immediately to prevent re-triggering
    exampleLoadedRef.current = true

    const loadExample = async () => {
      try {
        const { fileName, file } = await loadExampleProjectAsFile()
        // The example file is already a .tar.gz file, so just upload it directly
        setProjectName(fileName)
        onTarFileChange(file)
      } catch (error) {
        console.error('❌ Failed to load example:', error)
        BetterToast.error(
          `Failed to load example project: ${error instanceof Error ? error.message : 'Unknown error'}`
        )
        // Reset flag on error so it can be retried
        exampleLoadedRef.current = false
      }
    }

    loadExample()
  }, [withExamplePrefilled, workspaceUuid, projectName, onTarFileChange])

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      const isUploadActive = multiRepo
        ? repositories.some(r => r.status === 'uploading' || r.status === 'scanning')
        : uploadStatus !== null || projectName !== null
      if (!isUploadActive) {
        setDragOver(true)
      }
    },
    [uploadStatus, projectName, multiRepo, repositories]
  )

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      const isUploadActive = multiRepo
        ? repositories.some(r => r.status === 'uploading' || r.status === 'scanning')
        : uploadStatus !== null || projectName !== null
      if (isUploadActive) return

      setDragOver(false)
      const items = e.dataTransfer.items

      if (items && items.length > 0) {
        if (multiRepo) {
          // Multi-repository handling - process each directory separately
          for (let i = 0; i < items.length; i++) {
            const entry = items[i].webkitGetAsEntry()
            if (entry && entry.isDirectory) {
              const fileList: File[] = []
              const rootFolderName = entry.name

              const traverseFileTree = (item: FileSystemEntry, path: string): void => {
                if (item.isFile) {
                  ;(item as FileSystemFileEntry).file((file: File) => {
                    const newFile = new File([file], file.name, { type: file.type })
                    Object.defineProperty(newFile, 'webkitRelativePath', {
                      writable: true,
                      value: `${rootFolderName}/${path}${file.name}`
                    })
                    fileList.push(newFile)
                  })
                } else if (item.isDirectory) {
                  const dirReader = (item as FileSystemDirectoryEntry).createReader()
                  dirReader.readEntries((entries: FileSystemEntry[]) => {
                    for (let j = 0; j < entries.length; j++) {
                      traverseFileTree(entries[j], path + item.name + '/')
                    }
                  })
                }
              }

              traverseFileTree(entry, '')

              setTimeout(() => {
                if (fileList.length > 0) {
                  const dataTransfer = new DataTransfer()
                  fileList.forEach(file => dataTransfer.items.add(file))
                  handleMultipleFileSelection(dataTransfer.files)
                } else {
                  // Show error for empty folder
                  BetterToast.error(
                    'The selected folder is empty or contains no valid files. Please select a folder that contains files.'
                  )
                }
              }, 100)
            } else {
              BetterToast.error('Please drop a folder containing your Mule project')
            }
          }
        } else {
          // Single repository handling (existing logic)
          const entry = items[0].webkitGetAsEntry()
          if (entry && entry.isDirectory) {
            const fileList: File[] = []
            const rootFolderName = entry.name

            const traverseFileTree = (item: FileSystemEntry, path: string): void => {
              if (item.isFile) {
                ;(item as FileSystemFileEntry).file((file: File) => {
                  const newFile = new File([file], file.name, { type: file.type })
                  Object.defineProperty(newFile, 'webkitRelativePath', {
                    writable: true,
                    value: `${rootFolderName}/${path}${file.name}`
                  })
                  fileList.push(newFile)
                })
              } else if (item.isDirectory) {
                const dirReader = (item as FileSystemDirectoryEntry).createReader()
                dirReader.readEntries((entries: FileSystemEntry[]) => {
                  for (let i = 0; i < entries.length; i++) {
                    traverseFileTree(entries[i], path + item.name + '/')
                  }
                })
              }
            }

            traverseFileTree(entry, '')

            setTimeout(() => {
              if (fileList.length > 0) {
                const dataTransfer = new DataTransfer()
                fileList.forEach(file => dataTransfer.items.add(file))
                createCompressedTarArchive(dataTransfer.files, true)
              } else {
                // Show error for empty folder
                BetterToast.error(
                  'The selected folder is empty or contains no valid files. Please select a folder that contains files.'
                )
              }
            }, 100)
          } else {
            BetterToast.error('Please drop a folder containing your Mule project')
          }
        }
      }
    },
    [
      createCompressedTarArchive,
      multiRepo,
      handleMultipleFileSelection,
      repositories,
      uploadStatus,
      projectName
    ]
  )

  const handleFolderUpload = useCallback(async () => {
    const isUploadActive = multiRepo
      ? repositories.some(r => r.status === 'uploading' || r.status === 'scanning')
      : uploadStatus !== null || projectName !== null
    if (isUploadActive) return

    try {
      const result = await pickFolder('Select a project folder')
      if (!result) return

      const { tarFile, folderName } = result

      if (multiRepo) {
        const repo: RepositoryUpload = {
          id: Math.random().toString(36).substr(2, 9),
          name: tarFile.name,
          file: tarFile,
          progress: 0,
          status: 'pending',
          uploadStatus: 'Ready to upload'
        }
        setRepositories(prev => [...prev, repo])
        setTimeout(async () => {
          try {
            await uploadSingleRepository(repo)
          } catch (error) {
            console.error('Failed to upload repository:', error)
          }
        }, 10)
      } else {
        setProjectName(tarFile.name)
        onTarFileChange(tarFile)
      }
    } catch (error) {
      BetterToast.error(
        `Failed to process folder: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }, [
    pickFolder,
    multiRepo,
    repositories,
    uploadStatus,
    projectName,
    uploadSingleRepository,
    onTarFileChange
  ])

  // Multi-repository mode - check first to handle its own completion state
  if (multiRepo) {
    const completedCount = repositories.filter(r => r.status === 'completed').length
    const errorCount = repositories.filter(r => r.status === 'error').length
    const hasRepositories = repositories.length > 0
    const isUploading = repositories.some(r => r.status === 'uploading' || r.status === 'scanning')

    return (
      <div className={cm('multi-repo-upload', className)}>
        {/* Repository Chips - Horizontal Scrolling */}
        {hasRepositories && (
          <div className='mb-4'>
            <div className='flex items-center justify-between mb-2'>
              <h4 className='text-md font-medium select-none'>
                Repositories
                {completedCount > 0 && (
                  <span className='ml-4 font-normal text-gray-600 select-none'>
                    {completedCount} uploaded
                  </span>
                )}
                {errorCount > 0 && (
                  <span className='ml-4 font-normal text-gray-600 select-none'>
                    {errorCount} failed
                  </span>
                )}
              </h4>
            </div>

            {/* Horizontal Scrolling Chips */}
            <div className='flex space-x-2 overflow-x-auto pb-2'>
              {repositories.map(repo => (
                <RepositoryChip
                  key={repo.id}
                  repository={repo}
                  onRemove={removeRepository}
                  disabled={disabled || isUploading}
                />
              ))}
            </div>
          </div>
        )}

        {/* Drop Zone - Conditionally visible/disabled */}
        {!disabled && (
          <div
            className={cm(
              'upload-container my-2 w-full border-2 border-dashed rounded-2xl',
              isPlugin ? 'h-[100px]' : 'h-[140px]',
              dragOver ? 'border-[#6f7987] bg-[#e8effb]' : 'border-[#d4dce2]',
              'flex items-center justify-center',
              isUploading ? 'opacity-50 cursor-not-allowed' : ''
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div
              role='button'
              onClick={handleFolderUpload}
              className={cm(
                'upload-inner flex flex-col items-center justify-center',
                isPlugin ? 'w-[95%]' : 'w-[80%]',
                isUploading ? 'cursor-not-allowed' : 'cursor-pointer'
              )}
            >
              <FileUploadIcon />
              <div
                className={cm(
                  'description text-default-600 mt-2',
                  isPlugin ? 'text-xs' : 'text-sm'
                )}
              >
                <div className='flex justify-center flex-col items-center select-none'>
                  <div>
                    {hasRepositories ? (
                      <>
                        Drag and drop or <span className='text-link'>select</span> more repository
                        folders
                      </>
                    ) : (
                      <>
                        Drag and drop or <span className='text-link'>select</span> multiple
                        repository folders
                      </>
                    )}
                  </div>
                  <div className={cm('select-none', isPlugin && 'text-[10px] mt-1')}>
                    The code is used only for the purpose of the task and will be deleted once task
                    is done
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error message display */}
        {errorMessage && (
          <div className='flex items-center mt-1 text-sm text-red-600 mb-4'>
            <MdError className='mr-1' />
            {errorMessage}
          </div>
        )}
      </div>
    )
  }

  // Single repository completion UI - matches original behavior exactly
  if ((uploadStatus || projectName) && !isThreatsFound) {
    return (
      <div className={cm('w-full flex flex-col mb-4', className)}>
        <div className={cm('flex items-center justify-between')}>
          <div
            className={cm(
              'border border-[#d4dce2] rounded-2xl flex items-center min-w-[250px] flex-1',
              'overflow-hidden text-ellipsis',
              internalVirusScanStatus === VirusScanStatus.THREATS_FOUND ? 'border-red-400' : ''
            )}
          >
            <div className='file-type h-full border-r-1 border-[#d4dce2] py-2 px-5'>
              <FaFolder size={32} color='#6f7987' />
            </div>
            <div className='ml-2 flex-1 pr-2'>
              <div className='flex items-center gap-2'>
                {uploadStatus && (
                  <AiOutlineLoading className='animate-spin text-blue-600' size={16} />
                )}
                <div
                  className={cm(
                    'whitespace-nowrap text-sm text-default-700 select-text',
                    'overflow-hidden text-ellipsis'
                  )}
                >
                  {uploadStatus || projectName}
                </div>
              </div>
              {/* Show upload progress bar */}
              {uploadProgress !== null && uploadProgress < 100 && uploadStatus && (
                <div className='w-full mt-1'>
                  <div className='w-full bg-gray-200 rounded-full h-1.5'>
                    <div
                      className='bg-green-600 h-1.5 rounded-full transition-all duration-300'
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className='text-xs text-gray-500 mt-0.5 select-none'>
                    {uploadProgress.toFixed(0)}% uploaded
                  </div>
                </div>
              )}
              {/* Show virus scan progress bar */}
              {virusScanProgress !== null && uploadStatus && (
                <div className='w-full mt-1'>
                  <div className='w-full bg-gray-200 rounded-full h-1.5'>
                    <div
                      className='bg-blue-600 h-1.5 rounded-full transition-all duration-300'
                      style={{ width: `${virusScanProgress}%` }}
                    />
                  </div>
                  <div className='text-xs text-gray-500 mt-0.5 select-none'>
                    {virusScanProgress}% complete
                  </div>
                </div>
              )}
            </div>
          </div>
          {!uploadStatus && (
            <button
              className={cm(
                'button-submit bg-white text-default-600',
                'px-[10px] rounded-2xl cursor-pointer',
                'border border-[#d4dce2] select-none h-8 hover:bg-light3',
                'disabled:cursor-not-allowed',
                'w-12 h-12 ml-2 flex items-center justify-center',
                withExamplePrefilled ? 'opacity-50 cursor-not-allowed' : ''
              )}
              disabled={disabled}
              onClick={handleClear}
            >
              <GoTrash size={16} className='w-[16px] h-[16px]' />
            </button>
          )}
        </div>
      </div>
    )
  }

  // Check if upload is in progress (from any source - example or user upload)
  const isUploadInProgress = uploadStatus !== null || (projectName !== null && !singleRepoS3Path)

  return (
    <div className='flex flex-col w-full'>
      <div
        className={cm(
          'upload-container my-2 w-full border-2 border-dashed rounded-2xl',
          isPlugin ? 'h-[100px]' : 'h-[140px]',
          dragOver ? 'border-[#6f7987] bg-[#e8effb]' : 'border-[#d4dce2]',
          'flex items-center justify-center',
          isUploadInProgress ? 'opacity-50 cursor-not-allowed' : ''
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          role='button'
          onClick={handleFolderUpload}
          className={cm(
            'upload-inner flex flex-col items-center justify-center',
            isPlugin ? 'w-[95%]' : 'w-[80%]',
            isUploadInProgress ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
        >
          <FileUploadIcon />
          <div
            className={cm('description text-default-600 mt-2', isPlugin ? 'text-xs' : 'text-sm')}
          >
            <div className='flex justify-center flex-col items-center select-none'>
              <div>
                Drag and drop or <span className='text-link'>select</span> your Mule project folder
              </div>
              {multiRepo && (
                <div className='text-xs text-gray-500 mt-1 select-none'>
                  For multiple repos: drag multiple folders together
                </div>
              )}
              <div className={cm('select-none', isPlugin ? 'mt-1 text-[10px]' : 'mt-2')}>
                The code is used only for the purpose of the task and will be deleted once task is
                done
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error message display */}
      {errorMessage && (
        <div className='flex items-center mt-1 text-sm text-red-600 mb-4'>
          <MdError className='mr-1' />
          {errorMessage}
        </div>
      )}
    </div>
  )
}

// Repository Chip Component (for horizontal scrolling)
interface RepositoryChipProps {
  repository: RepositoryUpload
  onRemove: (id: string) => void
  disabled: boolean
}

function RepositoryChip({ repository, onRemove, disabled }: RepositoryChipProps) {
  const getChipColor = () => {
    switch (repository.status) {
      case 'completed':
        return 'border-transparent'
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'uploading':
      case 'scanning':
        return 'border-transparent'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getChipStyles = () => {
    switch (repository.status) {
      case 'completed':
        return {
          backgroundColor: 'rgba(159, 159, 248, 0.3)',
          color: 'rgba(0, 0, 0, 0.88)'
        }
      case 'uploading':
      case 'scanning':
        return {
          backgroundColor: 'rgba(159, 159, 248, 0.1)',
          color: 'rgba(0, 0, 0, 0.88)'
        }
      default:
        return {}
    }
  }

  const getStatusIcon = () => {
    switch (repository.status) {
      case 'completed':
        return '✓'
      case 'error':
        return <MdError className='text-red-500 w-3 h-3' />
      case 'uploading':
      case 'scanning':
        return '↑'
      default:
        return '○'
    }
  }

  return (
    <div
      className={cm(
        'flex items-center space-x-2 px-3 py-2 rounded-full border text-sm whitespace-nowrap',
        getChipColor(),
        'min-w-max'
      )}
      style={getChipStyles()}
    >
      <div className='flex items-center space-x-2'>
        <FaFolder className='w-3 h-3' />
        <span className='font-medium truncate max-w-[120px] select-text' title={repository.name}>
          {repository.name}
        </span>
        <span className='text-xs select-none'>{getStatusIcon()}</span>
      </div>

      {/* Progress bar for uploading */}
      {repository.status === 'uploading' && (
        <div className='w-8 h-1 bg-white rounded-full overflow-hidden'>
          <div
            className='h-full transition-all duration-300'
            style={{
              width: `${repository.progress}%`,
              backgroundColor: '#65558F'
            }}
          />
        </div>
      )}

      {/* Delete button */}
      <button
        onClick={() => onRemove(repository.id)}
        disabled={disabled || repository.status === 'uploading'}
        className={cm(
          'p-1 rounded-full hover:bg-black/10 disabled:opacity-50 disabled:cursor-not-allowed',
          'flex items-center justify-center w-5 h-5'
        )}
        title='Remove repository'
      >
        <GoTrash className='w-3 h-3' />
      </button>
    </div>
  )
}
